import React, { useState } from 'react';
import fr from 'facet-rest';
import StackItem from 'Components/ComponentLibrary/Atoms/Layout/StackItem';
import VerticalStack from 'Components/ComponentLibrary/Atoms/Layout/VerticalStack';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import Transition from 'Components/ComponentLibrary/Atoms/Transition';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Container from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Container';
import HeaderBar from 'Components/ComponentLibrary/Molecules/HeaderBar';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import featureEnabled, { pickFunction } from 'Helpers/Affordances';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import NoDataTemplate from 'Views/Page/Content/Templates/NoDataTemplate';
import FacetFacNegative from 'Icons/Brand/FacetFacNegative';

const Layout = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const {
    isPageContentLoading,
    loadPageContentLink,
    setPageContentCollection,
    pageContentResource: resource,
  } = usePageContentContext();
  const { householdResource } = useSimpleHousehold();
  const templateName = resource.type();
  const canPost = resource.meta().action('POST');
  const isLoading = isPageContentLoading || loading;

  const handleNext = async () => {
    logStandardizedEvent(categories.wizard, eventTypes.click, 'continue', { eventProperties: { templateName } });
    setErrors([]);
    setLoading(true);
    try {
      const updatedColl = await resource.submit();
      await setPageContentCollection(updatedColl);
    } catch (e) {
      setLoading(false);
      setErrors(e.errors);
    }
    setLoading(false);
  };

  const maybeHandlePrevious = pickFunction(resource, {
    [fr.relationships.previousLink.name]: () => async () => {
      const previousLink = resource
        .relationship(fr.relationships.previousLink.name)
        .link('related');

      logStandardizedEvent(categories.wizard, eventTypes.click, 'back', { eventProperties: { templateName } });
      await loadPageContentLink(previousLink);
    },
    [fr.relationships.default.name]: () => null,
  });


  if (!featureEnabled(householdResource, fr.relationships.facetPlan.name)) {
    return (
      <NoDataTemplate />
    );
  }

  return (
    <VerticalStack height="screen" spacing={3}>
      <StackItem>
        <HeaderBar
          test={`facet-plan-header-${templateName}`}
          title={'Scenario Selection'}
        />
      </StackItem>
      <StackItem fillsSpace>
        <Transition>
          <Container maxWidth="xs">
            <VerticalStack spacing={3}>
              <StackItem>
                <Box align="center">
                  <FacetFacNegative
                    style={{
                      width: 60,
                      height: '100%',
                    }}
                  />
                </Box>
              </StackItem>
              <StackItem>
                <Text
                  align="center"
                  text={resource.attr('title')}
                  variant="h1"
                  weight="bold"
                />
              </StackItem>
              <StackItem>
                <Text
                  align="center"
                  color="mediumGray"
                  text={resource.attr('subtitle')}
                  variant="h5"
                />
              </StackItem>
              <StackItem>
                <Text
                  align="center"
                  color="mediumGray"
                  text={resource.attr('text')}
                  variant="h5"
                />
              </StackItem>
            </VerticalStack>
          </Container>
        </Transition>
      </StackItem>
      <StackItem hidden={errors.length === 0}>
        <MetaFormInline
          noIndividualSubmitButtons
          resource={resource}
          LayoutProps={{
            hideErrorList: true,
          }}
          errorsOverride={errors}
        />
      </StackItem>
      <StackItem position="bottom">
        <StepNavigator
          disabled={isLoading}
          isLoading={isLoading}
          onNext={canPost ? handleNext : undefined}
          onPrevious={maybeHandlePrevious}
        >
          {'Get Started'}
        </StepNavigator>
      </StackItem>
    </VerticalStack>
  );
};

Layout.defaultProps = {};

Layout.propTypes = {};

export default Layout;
